import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { AlertTriangle, Clock, Calendar } from 'lucide-react'
import { useStages } from '@/hooks/use-picklists'
import { Badge } from '@/components/ui/badge'
import type { Opportunity } from '@/types'
import { toDisplayDate, toDateInputValue } from '@/lib/utils'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

// Pipeline stage colours per GRK-BRAND-1 (sequential blue ramp, order carries
// meaning). Keys match opportunity.stage_name. Unknown stages use slate.
// Visual identity: tint fill (light) + solid left border (dark).
const STAGE_PALETTE: Record<
  string,
  { backgroundColor: string; borderColor: string; borderLeftColor: string }
> = {
  // Early stages: Powder (light) → Steel
  'Need Identified': {
    backgroundColor: CATEGORICAL.powder.tint,
    borderColor: CATEGORICAL.powder.solid,
    borderLeftColor: CATEGORICAL.powder.solid,
  },
  // Mid stages: Steel → Navy
  'Presentation Complete': {
    backgroundColor: CATEGORICAL.steel.tint,
    borderColor: CATEGORICAL.steel.solid,
    borderLeftColor: CATEGORICAL.steel.solid,
  },
  'Initial Quote Complete': {
    backgroundColor: CATEGORICAL.navy.tint,
    borderColor: CATEGORICAL.navy.solid,
    borderLeftColor: CATEGORICAL.navy.solid,
  },
  // Late stages: Navy → Prussian
  'Final Quote': {
    backgroundColor: CATEGORICAL.navy.tint,
    borderColor: CATEGORICAL.navy.solid,
    borderLeftColor: CATEGORICAL.prussian.solid,
  },
  'Verbal Received': {
    backgroundColor: CATEGORICAL.prussian.tint,
    borderColor: CATEGORICAL.prussian.solid,
    borderLeftColor: CATEGORICAL.prussian.solid,
  },
}
const DEFAULT_STAGE_PALETTE = {
  backgroundColor: CATEGORICAL.slate.tint,
  borderColor: CATEGORICAL.slate.solid,
  borderLeftColor: CATEGORICAL.slate.solid,
}

interface OpportunityKanbanProps {
  opportunities: Opportunity[]
  onStageChange: (id: string, newStage: string) => Promise<void>
  isLoading?: boolean
}

// Closed stages to exclude from Kanban
const CLOSED_STAGES = new Set(['Closed Won', 'Closed Lost', 'Service (Do not Use)'])

interface DroppableColumnProps {
  stageName: string
  stageLabel: string
  opportunities: Opportunity[]
  count: number
  totalValue: number
  currency: string
}

interface DraggableCardProps {
  opportunity: Opportunity
}

function DraggableCard({ opportunity }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: opportunity.id,
    data: { stageName: opportunity.stage_name },
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  }

  // Compute indicator flags. Compare LOCAL calendar dates (toDateInputValue),
  // not the UTC date, so close dates aren't flagged overdue a day early/late.
  const today = toDateInputValue(new Date())
  const isOverdue =
    !!opportunity.close_date &&
    toDateInputValue(opportunity.close_date) < today &&
    !opportunity.is_closed
  const hasSlippage =
    ((opportunity as unknown as { slippage_count?: number }).slippage_count ?? 0) > 0
  const noTask = !(opportunity as unknown as { next_task_date?: string | null }).next_task_date

  const formatDate = (dateStr: string | undefined) => {
    const d = toDisplayDate(dateStr)
    if (!d) return ''
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="listitem"
      tabIndex={0}
      className="bg-background rounded-md border p-2.5 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none touch-none"
      aria-describedby="kanban-keyboard-help"
    >
      {/* Opportunity Name (link) */}
      <Link
        to={`/opportunities/${opportunity.id}`}
        className="block font-semibold text-xs leading-tight hover:underline line-clamp-2 text-foreground"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {opportunity.name || opportunity.opportunity_name}
      </Link>

      {/* Account Name (link) */}
      {opportunity.account_id && (
        <Link
          to={`/accounts/${opportunity.account_id}`}
          className="block text-[11px] text-muted-foreground truncate mt-0.5 hover:underline"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {opportunity.account_name}
        </Link>
      )}

      {/* Amount — Navy (no green in the palette) */}
      <p className="text-xs font-medium mt-1.5" style={{ color: GRACKLE_COLORS.navy }}>
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: opportunity.currency_iso_code || 'CAD',
        }).format(opportunity.amount ?? 0)}
      </p>

      {/* Close Date */}
      {opportunity.close_date && (
        <div className="flex items-center gap-1 mt-1">
          <Calendar className="h-2.5 w-2.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-[11px] text-muted-foreground">
            {formatDate(opportunity.close_date)}
          </span>
        </div>
      )}

      {/* Warning chips — visible text labels so the meaning is not conveyed
          by colour/shape alone (WCAG 1.4.1) and is discoverable without
          needing a hover tooltip (WCAG 1.4.13). */}
      {(isOverdue || hasSlippage || noTask) && (
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          {isOverdue && (
            <span
              className="inline-flex items-center gap-0.5 rounded bg-destructive/10 text-destructive px-1 py-0.5 text-[10px] font-medium"
              aria-label={`Overdue — close date was ${formatDate(opportunity.close_date)}`}
            >
              <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
              Overdue
            </span>
          )}
          {hasSlippage && (
            <span
              className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: CATEGORICAL.pollen.tint,
                color: GRACKLE_COLORS.prussianBlue,
              }}
              aria-label={`Close date pushed ${(opportunity as unknown as { slippage_count?: number }).slippage_count}×`}
            >
              <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
              Pushed {(opportunity as unknown as { slippage_count?: number }).slippage_count}×
            </span>
          )}
          {noTask && (
            <span
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold"
              style={{
                backgroundColor: CATEGORICAL.clay.tint,
                color: GRACKLE_COLORS.prussianBlue,
              }}
              aria-label="No task scheduled"
            >
              <Clock className="h-3 w-3" aria-hidden="true" />
              No task scheduled
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function DroppableColumn({
  stageName,
  stageLabel,
  opportunities,
  count,
  totalValue,
  currency,
}: DroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${stageName}`,
    data: { type: 'column', stageName },
  })

  const stagePalette = STAGE_PALETTE[stageName] ?? DEFAULT_STAGE_PALETTE

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-w-0 rounded-lg border-2 p-2 transition-colors ${
        isOver ? 'ring-2 ring-primary ring-offset-1' : ''
      }`}
      style={{
        backgroundColor: stagePalette.backgroundColor,
        borderColor: stagePalette.borderColor,
        borderLeftWidth: '4px',
        borderLeftColor: stagePalette.borderLeftColor,
      }}
      role="list"
      aria-label={`Stage: ${stageLabel}`}
    >
      {/* Column Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-xs truncate">{stageLabel}</h3>
          <Badge variant="secondary" className="text-[10px] h-5 px-1.5 shrink-0">
            {count}
          </Badge>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0,
          }).format(totalValue)}
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-1.5 min-h-[60px]">
        {opportunities.length === 0 ? (
          <p
            className="text-[10px] text-muted-foreground text-center py-3"
            aria-label="No opportunities in this stage"
          >
            No opportunities
          </p>
        ) : (
          opportunities.map((opp) => <DraggableCard key={opp.id} opportunity={opp} />)
        )}
      </div>
    </div>
  )
}

export function OpportunityKanban({
  opportunities,
  onStageChange,
  isLoading,
}: OpportunityKanbanProps) {
  const { stages } = useStages()

  // Filter to open stages only
  const openStages = useMemo(() => {
    return stages.filter((s) => !CLOSED_STAGES.has(s.value))
  }, [stages])

  // Group opportunities by stage
  const byStage = useMemo(() => {
    const map = new Map<string, Opportunity[]>()
    for (const s of openStages) {
      map.set(s.value, [])
    }
    for (const opp of opportunities) {
      const bucket = map.get(opp.stage_name)
      if (bucket) {
        bucket.push(opp)
      }
      // Opportunities with closed or unknown stage_name are not rendered
    }
    return map
  }, [openStages, opportunities])

  // Drag-drop sensors. PointerSensor with a small activation distance so clicks
  // on the card links still work without triggering a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor)
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over) return

    const sourceStage = (active.data.current as { stageName?: string } | undefined)?.stageName
    if (!sourceStage) return

    // Destination: either a column (type:'column') or another card (falls back to its stage).
    const overData = over.data.current as { type?: string; stageName?: string } | undefined
    const destStage = overData?.type === 'column' ? overData.stageName : overData?.stageName
    if (!destStage || sourceStage === destStage) return

    await onStageChange(active.id as string, destStage)
  }

  if (isLoading) {
    // Skeleton loader: 3 columns × 3 shimmer cards
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {[1, 2, 3].map((col) => (
          <div key={col} className="flex-shrink-0 w-80 bg-muted/30 rounded-lg p-3 animate-pulse">
            <div className="h-4 bg-muted rounded w-1/2 mb-2" />
            <div className="h-3 bg-muted rounded w-1/3 mb-3" />
            {[1, 2, 3].map((card) => (
              <div key={card} className="bg-card border rounded-lg p-3 mb-2">
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2 mb-2" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <section role="region" aria-label="Opportunities by stage">
      {/* Keyboard usage help (visually hidden) */}
      <p id="kanban-keyboard-help" className="sr-only">
        Drag to another stage, or use keyboard: Space to pick up, Arrow keys to move, Space to drop,
        Escape to cancel.
      </p>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={pointerWithin}>
        <div className="flex gap-2 overflow-x-auto pb-4">
          {openStages.map((stage) => {
            const oppsInStage = byStage.get(stage.value) || []
            // Stage totals can mix currencies, so sum the CAD-converted amount (amount_cad)
            // from the API and label the total in CAD. Per-card amounts still show native currency.
            const totalValue = oppsInStage.reduce((sum, o) => sum + (o.amount_cad ?? 0), 0)
            const currency = 'CAD'

            return (
              <DroppableColumn
                key={stage.value}
                stageName={stage.value}
                stageLabel={stage.label}
                opportunities={oppsInStage}
                count={oppsInStage.length}
                totalValue={totalValue}
                currency={currency}
              />
            )
          })}
        </div>
      </DndContext>
    </section>
  )
}
